import Skeleton from "react-loading-skeleton";
import { useParams } from "react-router";
import { motion } from "framer-motion";

import Flex from "core/components/Flex";
import InfoFlash from "core/components/InfoFlash";
import Pill from "core/components/Pill";
import Typography from "core/components/Typography";
import { PERMISSIONS, ROLES } from "core/constants";
import useActiveMembership from "core/hooks/useActiveMembership";
import {
  useCustomerSegmentListQuery,
  useOrganizationByIdQuery,
  useOrganizationDeploymentQuery,
  useOrganizationOnboardingQuery,
} from "core/queries";
import {
  DEPLOYMENT_STATUSES,
  ONBOARDING_LABELS,
  ONBOARDING_VARIANTS,
} from "modules/admin/OrgProductLicenseForm/constants";
import useHasPermissions from "core/hooks/useHasPermissions";
import DetailsViewer from "modules/admin/DetailsViewer";
import { getEnv, ENVIRONMENTS } from "common/utilities";
import GeneralForm from "modules/admin/OrgDetailsForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import { setupOrganization, updateOrganization } from "modules/admin/actions";
import { toast } from "sonner";

const OrganizationGeneralPage = () => {
  const { orgId: organizationId } = useParams();

  const queryClient = useQueryClient();
  const { activeMembership } = useActiveMembership();

  const { data: organization, isLoading: isOrganizationLoading } =
    useOrganizationByIdQuery({
      organizationId,
    });

  // TODO: Refetch intervals
  const { data: onboarding, isLoading: isOnboardingLoading } =
    useOrganizationOnboardingQuery({
      organizationId,
      select: (data) => {
        const formattedData = data;
        //   const formattedData = buildStatusObject(data, ONBOARDING_LABELS);

        return {
          ...formattedData,
          tooltip: null,
          //   <StatusTooltip
          //     data={formattedData}
          //     onCodeClick={
          //       activeMembership.role === ROLES.systemAdmin
          //         ? () =>
          //             openModalWithData(
          //               {
          //                 onboarding: organization.onboarding,
          //               },
          //               organizationId
          //             )
          //         : null
          //     }
          //   />
        };
      },
    });
  const { data: deployment, isLoading: isDeploymentLoading } =
    useOrganizationDeploymentQuery({
      organizationId,
      select: (data) => {
        const formattedData = data;
        //   const formattedData = buildStatusObject(data, ONBOARDING_LABELS);

        return {
          ...formattedData,
          tooltip: null,
          //   <StatusTooltip
          //     data={formattedData}
          //     onCodeClick={
          //       activeMembership.role === ROLES.systemAdmin
          //         ? () =>
          //             openModalWithData(
          //               {
          //                 onboarding: organization.onboarding,
          //               },
          //               organizationId
          //             )
          //         : null
          //     }
          //   />
        };
      },
    });
  const { data: organizationSegment, isLoading: isSegmentLoading } =
    useCustomerSegmentListQuery({
      enabled: organization?.customer_segment,
      select: (data) => {
        const customerSegment = Object.values(data).find(
          (item) => item.id === organization?.customer_segment
        );
        const customerSegmentName = customerSegment?.name;

        const isMedicareLicensable = Object.values(data).find(
          (item) => item.id === organization?.customer_segment
        )?.is_medicare_licensable;

        const env = getEnv();
        const isDevEnv = [ENVIRONMENTS.LOCAL, ENVIRONMENTS.DEV].includes(env);
        const isPrOrg =
          isDevEnv && customerSegment?.pr_org === organization?.id;
        const licensableProductIds = customerSegment.licensable_products;

        return {
          customerSegmentName,
          licensableProductIds,
          isMedicareLicensable,
          isPrOrg,
        };
      },
    });

  const retryOrgSetup = useAuthenticatedMutation((req) =>
    setupOrganization({ ...req, organizationId })
  );
  const setupRetryMutation = useMutation({
    mutationFn: retryOrgSetup,
    onSuccess: async (data, org) => {
      // Notify
      toast.success("Organization setup retry has been started.");
    },
    onError: async (error, variables, context) => {
      console.error("setupRetryMutation", { error, variables, context });
      // Notify
      toast.error("Organization setup retry failed.");
    },
    onSettled: () => {
      // Optimistically update status to in progress
      queryClient.setQueryData(
        ["organization-onboarding", { organizationId }],
        (previous) => {
          return { ...previous, status: "in progress" };
        }
      );
    },
  });
  const updateOrg = useAuthenticatedMutation((req) =>
    updateOrganization({
      ...req,
      organizationId,
    })
  );
  const orgMutation = useMutation({
    mutationFn: updateOrg,
    onSuccess: (data, updatedOrg) => {
      /**
       * Since API calls are very slow, we update the cache with the updates for both the
       * single org's cache, and the org list.
       *
       * Then when we invalidate both queries since we don't receive full documents from response.
       *
       */

      // Update local list and detail query cache
      queryClient.setQueryData(
        ["organizations", { organizationId }],
        (cachedOrg) => {
          if (!cachedOrg) {
            return {
              ...updatedOrg,
              isUpdated: true,
              resetUpdateParams: {
                queryKey: ["organizations", { organizationId }],
                rowId: organizationId,
              },
            };
          }

          return {
            ...cachedOrg,
            ...updatedOrg,
            isUpdated: true,
            resetUpdateParams: {
              queryKey: ["organizations", { organizationId }],
              rowId: organizationId,
            },
          };
        }
      );

      queryClient.setQueryData(["organizations"], (cachedOrgList) => {
        if (!cachedOrgList || cachedOrgList.length === 0) {
          return [
            {
              ...updatedOrg,
              isUpdated: true,
              resetUpdateParams: {
                queryKey: ["organizations"],
                rowId: organizationId,
              },
            },
          ];
        }

        return cachedOrgList.map((cachedOrg) => {
          if (cachedOrg.id === organizationId) {
            return {
              ...cachedOrg,
              ...updatedOrg,
              isUpdated: true,
              resetUpdateParams: {
                queryKey: ["organizations"],
                rowId: organizationId,
              },
            };
          }

          return cachedOrg;
        });
      });

      // Invalidate list and detail queries
      queryClient.invalidateQueries({ queryKey: ["organizations"] });

      // Notify
      toast.success("Organization has been updated.");
    },
  });

  const canUpdateOrgGeneral = useHasPermissions(
    PERMISSIONS[activeMembership.role],
    "organization:update"
  );

  const isPageLoading = [
    isOrganizationLoading,
    isOnboardingLoading,
    isDeploymentLoading,
    isSegmentLoading,
  ].some((isQueryLoading) => isQueryLoading);

  return (
    <div style={{ maxWidth: "500px" }}>
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        marginBottom="5px"
      >
        <Typography variant="h2" noMargin>
          General
        </Typography>

        <div>
          {onboarding ? (
            <Pill
              text={ONBOARDING_LABELS[onboarding.status]}
              size="large"
              variant={ONBOARDING_VARIANTS[onboarding.status]}
              tooltipId={`details-${organizationId}`}
              textDataTest="onboardingStatus"
              tooltipContent={
                organization?.active
                  ? ["succeeded", "timeout", "failed"].includes(
                      onboarding.rawStatus
                    )
                    ? onboarding.tooltip
                    : null
                  : null
              }
            />
          ) : (
            <Skeleton style={{ width: "50px", height: "30px" }} />
          )}
        </div>
      </Flex>

      {["queued", "in progress"].includes(onboarding?.status) ? (
        <InfoFlash
          style={{
            marginTop: "12px",
            marginBottom: "16px",
          }}
        >
          This page is restricted to read-only while onboarding is in progress.
        </InfoFlash>
      ) : [
          DEPLOYMENT_STATUSES.QUEUED,
          DEPLOYMENT_STATUSES.IN_PROGRESS,
        ].includes(deployment?.status) ? (
        <InfoFlash
          style={{
            marginTop: "12px",
            marginBottom: "16px",
          }}
        >
          This page is restricted to read-only while a deployment is in
          progress.
        </InfoFlash>
      ) : null}

      {canUpdateOrgGeneral &&
      ![DEPLOYMENT_STATUSES.QUEUED, DEPLOYMENT_STATUSES.IN_PROGRESS].includes(
        deployment?.status
      ) &&
      !["queued", "in progress"].includes(onboarding?.status) ? (
        <GeneralForm
          onSubmit={orgMutation.mutate}
          onCancel={
            ["succeeded", "in progress"].includes(onboarding?.status)
              ? null
              : setupRetryMutation.mutate
          }
          initialValues={organization}
          error={orgMutation.error}
          isFetching={isPageLoading}
          isSubmitting={orgMutation.isPending}
          isCancelling={setupRetryMutation.isPending}
        />
      ) : (
        <DetailsViewer
          isLoading={isPageLoading}
          data={[
            {
              label: "Organization Name",
              value: organization?.name,
            },
            {
              label: "Organization ID",
              value: organization?.id,
            },
            {
              label: "Primary Contact",
              value: organization?.primaryContact?.name,
            },
            {
              label: "Primary Email",
              value: organization?.primaryContact?.email,
            },
            {
              label: "Domain",
              value: organization?.domain,
            },
            {
              label: "Customer Segment",
              value: organizationSegment?.customerSegmentName,
            },
            ...(organizationSegment?.isMedicareLicensable
              ? [
                  {
                    label: "Is Medicare QE Included?",
                    value: organization?.medicare_licensed ? "Yes" : "No",
                  },
                ]
              : []),
          ]}
        />
      )}
    </div>
  );
};

export default OrganizationGeneralPage;
